import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';

// ── Types ────────────────────────────────────────────────────────────────────

interface AccountingStatus {
  connected: boolean;
  provider: string | null;
  realmId?: string;
  syncMode?: string;
  lastSyncAt?: string | null;
  companyName?: string | null;
  unmappedFeeCount?: number;
  recentSyncs?: SyncLogEntry[];
}

interface SyncLogEntry {
  id: number;
  settlementId: string;
  journalType: string;
  syncStatus: string;
  qbJournalId: string | null;
  journalDate: string | null;
  journalAmount: number | null;
  marketplace: string | null;
  errorMessage: string | null;
  voidedAt: string | null;
  syncedAt: string | null;
}

interface FeeMapping {
  id: number;
  amazonFeeType: string;
  qbAccountId: string;
  qbAccountName: string | null;
  isDefault: number;
}

interface KnownFeeType {
  amazonFeeType: string;
  defaultAccount: string;
  accountType: string;
  description: string;
}

interface QBAccount {
  id: string;
  name: string;
  type: string;
  subType?: string;
}

interface UnsyncedSettlement {
  settlementId: string;
  settlementStart: string;
  settlementEnd: string;
  depositDate: string;
  totalAmount: number;
  currency: string;
  itemCount: number;
  previousStatus: string | null;
  previousError: string | null;
}

interface SyncResult {
  settlementId: string;
  journalType: string;
  status: string;
  qbJournalId?: string;
  error?: string;
}

interface Account {
  id: string;
  name: string;
}

interface UnmappedFee {
  id: number;
  feeKey: string;
  totalAmount: number;
  occurrenceCount: number;
  lastSettlementId: string;
  createdAt: string;
}

interface ReconciliationEntry {
  settlementId: string;
  settlementStart: string;
  settlementEnd: string;
  depositDate: string;
  totalAmount: number;
  currency: string;
  journals: {
    journalType: string;
    status: string;
    qbJournalId: string | null;
    journalDate: string | null;
    journalAmount: number | null;
    voidedAt: string | null;
  }[];
}

type TabKey = 'sync' | 'reconciliation' | 'mappings' | 'history';

// ── Component ────────────────────────────────────────────────────────────────

export function FinanceAccounting() {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [status, setStatus] = useState<AccountingStatus | null>(null);
  const [mappings, setMappings] = useState<FeeMapping[]>([]);
  const [, setKnownFeeTypes] = useState<KnownFeeType[]>([]);
  const [qbAccounts, setQbAccounts] = useState<QBAccount[]>([]);
  const [syncLog, setSyncLog] = useState<SyncLogEntry[]>([]);
  const [unsyncedSettlements, setUnsyncedSettlements] = useState<UnsyncedSettlement[]>([]);
  const [selectedSettlements, setSelectedSettlements] = useState<Set<string>>(new Set());
  const [syncResults, setSyncResults] = useState<SyncResult[]>([]);
  const [unmappedFees, setUnmappedFees] = useState<UnmappedFee[]>([]);
  const [reconciliation, setReconciliation] = useState<ReconciliationEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [activeTab, setActiveTab] = useState<TabKey>('sync');

  useEffect(() => {
    api.get<Account[]>('/accounts').then((accts) => {
      setAccounts(accts);
      if (accts.length > 0) setSelectedAccount(accts[0].id);
      else setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedAccount) return;
    loadAll();
  }, [selectedAccount]);

  async function loadAll() {
    setLoading(true);
    setError('');
    try {
      const [statusData, mappingData, logData, unsyncedData] = await Promise.all([
        api.get<AccountingStatus>(`/finance/accounting/${selectedAccount}`),
        api.get<{ mappings: FeeMapping[]; knownFeeTypes: KnownFeeType[] }>(`/finance/accounting/${selectedAccount}/mappings`),
        api.get<{ syncLog: SyncLogEntry[] }>(`/finance/accounting/${selectedAccount}/sync-log`),
        api.get<{ settlements: UnsyncedSettlement[] }>(`/finance/accounting/${selectedAccount}/unsynced`).catch(() => ({ settlements: [] })),
      ]);
      setStatus(statusData);
      setMappings(mappingData.mappings);
      setKnownFeeTypes(mappingData.knownFeeTypes);
      setSyncLog(logData.syncLog);
      setUnsyncedSettlements(unsyncedData.settlements);
      setSelectedSettlements(new Set());
      setSyncResults([]);

      if (statusData.connected) {
        // Load extra data in parallel
        const [qbData, unmappedData, reconData] = await Promise.all([
          api.get<{ accounts: QBAccount[] }>(`/finance/accounting/${selectedAccount}/qb-accounts`).catch(() => ({ accounts: [] })),
          api.get<{ unmappedFees: UnmappedFee[] }>(`/finance/accounting/${selectedAccount}/unmapped-fees`).catch(() => ({ unmappedFees: [] })),
          api.get<{ reconciliation: ReconciliationEntry[] }>(`/finance/accounting/${selectedAccount}/reconciliation`).catch(() => ({ reconciliation: [] })),
        ]);
        setQbAccounts(qbData.accounts);
        setUnmappedFees(unmappedData.unmappedFees);
        setReconciliation(reconData.reconciliation);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  async function handleDisconnect() {
    try {
      await api.delete(`/finance/accounting/${selectedAccount}`);
      setFeedback('QuickBooks disconnected.');
      loadAll();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to disconnect');
    }
  }

  async function handleSyncModeToggle() {
    const newMode = status?.syncMode === 'summary' ? 'detailed' : 'summary';
    try {
      await api.put(`/finance/accounting/${selectedAccount}/sync-mode`, { syncMode: newMode });
      setStatus((prev) => prev ? { ...prev, syncMode: newMode } : prev);
      setFeedback(`Sync mode changed to ${newMode}.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update sync mode');
    }
  }

  function toggleSettlement(id: string) {
    setSelectedSettlements((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function toggleAllSettlements() {
    if (selectedSettlements.size === unsyncedSettlements.length) {
      setSelectedSettlements(new Set());
    } else {
      setSelectedSettlements(new Set(unsyncedSettlements.map((s) => s.settlementId)));
    }
  }

  async function handleSyncSelected() {
    if (selectedSettlements.size === 0) return;
    setSyncing(true);
    setSyncResults([]);
    try {
      const result = await api.post<{ message: string; results: SyncResult[] }>(
        `/finance/accounting/${selectedAccount}/sync`,
        { settlementIds: Array.from(selectedSettlements) },
      );
      setFeedback(result.message);
      setSyncResults(result.results);
      loadAll();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sync');
    } finally {
      setSyncing(false);
    }
  }

  async function handleVoid(syncLogId: number) {
    try {
      const result = await api.post<{ message: string }>(`/finance/accounting/${selectedAccount}/void`, { syncLogId });
      setFeedback(result.message);
      loadAll();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to void');
    }
  }

  async function handleResolveUnmapped(id: number) {
    try {
      await api.post(`/finance/accounting/${selectedAccount}/unmapped-fees/${id}/resolve`);
      setUnmappedFees(prev => prev.filter(f => f.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resolve');
    }
  }

  async function handleResetMappings() {
    try {
      await api.post(`/finance/accounting/${selectedAccount}/mappings/reset`);
      setFeedback('Mappings reset to defaults (includes COGS + Carried Forward accounts).');
      loadAll();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset');
    }
  }

  async function handleDeleteMapping(id: number) {
    try {
      await api.delete(`/finance/accounting/${selectedAccount}/mappings/${id}`);
      setMappings((prev) => prev.filter((m) => m.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete');
    }
  }

  async function handleSaveMapping(amazonFeeType: string, qbAccountId: string, qbAccountName: string) {
    try {
      await api.post(`/finance/accounting/${selectedAccount}/mappings`, {
        amazonFeeType, qbAccountId, qbAccountName,
      });
      setFeedback(`Mapping saved: ${amazonFeeType} → ${qbAccountName}`);
      loadAll();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    }
  }

  if (loading) return <LoadingSkeleton />;

  const tabs: { key: TabKey; label: string; badge?: number }[] = [
    { key: 'sync', label: 'Sync', badge: unsyncedSettlements.length || undefined },
    { key: 'reconciliation', label: 'Reconciliation' },
    { key: 'mappings', label: 'Fee Mapping', badge: unmappedFees.length || undefined },
    { key: 'history', label: 'History', badge: syncLog.filter(l => l.syncStatus === 'failed').length || undefined },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold">Accounting Integration</h2>
          <p className="text-sm text-gray-500">QuickBooks Online — settlement journals, COGS posting, marketplace segmentation.</p>
        </div>
        {accounts.length > 1 && (
          <select
            value={selectedAccount}
            onChange={(e) => setSelectedAccount(e.target.value)}
            className="rounded-lg border border-gray-200 px-3 py-2 text-sm"
          >
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>{a.name || a.id}</option>
            ))}
          </select>
        )}
      </div>

      {/* Feedback */}
      {feedback && (
        <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-800">
          {feedback}
          <button onClick={() => setFeedback('')} className="ml-2 text-green-600 hover:text-green-800">x</button>
        </div>
      )}
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {error}
          <button onClick={() => setError('')} className="ml-2 text-red-600 hover:text-red-800">x</button>
        </div>
      )}

      {/* Unmapped Fee Alert */}
      {unmappedFees.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-amber-600 text-lg">!</span>
            <span className="text-sm font-medium text-amber-800">{unmappedFees.length} Unmapped Fee Type{unmappedFees.length > 1 ? 's' : ''}</span>
          </div>
          <p className="text-xs text-amber-700 mb-3">These Amazon fee types don't have a QuickBooks account mapped. They're posted to "Uncategorized Amazon" until mapped.</p>
          <div className="space-y-1">
            {unmappedFees.map(f => (
              <div key={f.id} className="flex items-center justify-between rounded bg-white/50 px-3 py-1.5">
                <div>
                  <span className="font-mono text-xs text-amber-900">{f.feeKey}</span>
                  <span className="ml-2 text-xs text-amber-600">({f.occurrenceCount}x in settlements)</span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => { setActiveTab('mappings'); }}
                    className="text-xs text-brand-600 hover:text-brand-800"
                  >
                    Map Now
                  </button>
                  <button
                    onClick={() => handleResolveUnmapped(f.id)}
                    className="text-xs text-gray-400 hover:text-gray-600"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Connection Status */}
      <div className="rounded-xl border border-gray-200 bg-white p-6">
        <h3 className="text-sm font-semibold mb-4">Connection</h3>
        {status?.connected ? (
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-700">Connected</span>
              <span className="text-sm text-gray-600">QuickBooks Online</span>
              {status.companyName && <span className="text-sm font-medium">{status.companyName}</span>}
            </div>
            <div className="flex items-center gap-3 text-xs text-gray-500">
              <span>Realm: {status.realmId}</span>
              <span>Mode: <strong>{status.syncMode === 'detailed' ? 'Per-SKU' : 'Summary'}</strong></span>
              {status.lastSyncAt && <span>Last sync: {status.lastSyncAt?.slice(0, 16)}</span>}
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <span>Features: Accrual month-split, COGS (FIFO), Marketplace Classes</span>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={handleSyncModeToggle} className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs hover:bg-gray-50">
                Switch to {status.syncMode === 'summary' ? 'Per-SKU (Detailed)' : 'Summary'} mode
              </button>
              <button onClick={handleDisconnect} className="rounded-lg border border-red-200 px-3 py-1.5 text-xs text-red-600 hover:bg-red-50">
                Disconnect
              </button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-500">
            No QuickBooks connection. Go to <a href="/credentials" className="text-brand-600 hover:text-brand-800 underline">Credentials</a> to connect.
          </p>
        )}
      </div>

      {/* Tabs */}
      {status?.connected && (
        <>
          <div className="flex gap-1 border-b border-gray-200">
            {tabs.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.key
                    ? 'border-brand-600 text-brand-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
                {tab.badge !== undefined && tab.badge > 0 && (
                  <span className={`ml-1.5 inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium ${
                    tab.key === 'mappings' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {tab.badge}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          {activeTab === 'sync' && (
            <SyncTab
              unsyncedSettlements={unsyncedSettlements}
              selectedSettlements={selectedSettlements}
              syncResults={syncResults}
              syncing={syncing}
              onToggle={toggleSettlement}
              onToggleAll={toggleAllSettlements}
              onSync={handleSyncSelected}
            />
          )}

          {activeTab === 'reconciliation' && (
            <ReconciliationTab entries={reconciliation} />
          )}

          {activeTab === 'mappings' && (
            <MappingsTab
              mappings={mappings}
              qbAccounts={qbAccounts}
              onSave={handleSaveMapping}
              onDelete={handleDeleteMapping}
              onReset={handleResetMappings}
            />
          )}

          {activeTab === 'history' && (
            <HistoryTab syncLog={syncLog} onVoid={handleVoid} />
          )}
        </>
      )}
    </div>
  );
}

// ── Sync Tab ─────────────────────────────────────────────────────────────────

function SyncTab({
  unsyncedSettlements, selectedSettlements, syncResults, syncing,
  onToggle, onToggleAll, onSync,
}: {
  unsyncedSettlements: UnsyncedSettlement[];
  selectedSettlements: Set<string>;
  syncResults: SyncResult[];
  syncing: boolean;
  onToggle: (id: string) => void;
  onToggleAll: () => void;
  onSync: () => void;
}) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold">Sync to QuickBooks</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Each settlement creates journal entries: settlement JE (accrual month-split if cross-month) + COGS JE (FIFO).
          </p>
        </div>
        <button
          onClick={onSync}
          disabled={syncing || selectedSettlements.size === 0}
          className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
        >
          {syncing ? 'Syncing...' : `Sync Selected (${selectedSettlements.size})`}
        </button>
      </div>

      {/* Sync Results */}
      {syncResults.length > 0 && (
        <div className="mb-4 space-y-1">
          {syncResults.map((r, i) => (
            <div key={`${r.settlementId}-${r.journalType}-${i}`} className={`rounded-lg px-3 py-2 text-xs ${
              r.status === 'synced'
                ? 'bg-green-50 border border-green-200 text-green-800'
                : 'bg-red-50 border border-red-200 text-red-800'
            }`}>
              <span className="font-mono">{r.settlementId}</span>
              <JournalTypeBadge type={r.journalType} />
              {r.status === 'synced'
                ? <span> — Journal #{r.qbJournalId}</span>
                : <span> — Failed: {r.error}</span>
              }
            </div>
          ))}
        </div>
      )}

      {unsyncedSettlements.length === 0 ? (
        <p className="text-sm text-gray-500">All settlements are synced. Nothing to push.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs font-medium uppercase text-gray-500">
              <tr>
                <th className="px-3 py-2 w-8">
                  <input
                    type="checkbox"
                    checked={selectedSettlements.size === unsyncedSettlements.length && unsyncedSettlements.length > 0}
                    onChange={onToggleAll}
                    className="rounded border-gray-300"
                  />
                </th>
                <th className="px-3 py-2">Settlement ID</th>
                <th className="px-3 py-2">Period</th>
                <th className="px-3 py-2">Deposit Date</th>
                <th className="px-3 py-2 text-right">Amount</th>
                <th className="px-3 py-2">Cross-Month</th>
                <th className="px-3 py-2">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {unsyncedSettlements.map((s) => {
                const startMonth = s.settlementStart?.slice(0, 7);
                const endMonth = s.settlementEnd?.slice(0, 7);
                const crossMonth = startMonth && endMonth && startMonth !== endMonth;

                return (
                  <tr
                    key={s.settlementId}
                    className={`hover:bg-gray-50 cursor-pointer ${selectedSettlements.has(s.settlementId) ? 'bg-brand-50' : ''}`}
                    onClick={() => onToggle(s.settlementId)}
                  >
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={selectedSettlements.has(s.settlementId)}
                        onChange={() => onToggle(s.settlementId)}
                        onClick={(e) => e.stopPropagation()}
                        className="rounded border-gray-300"
                      />
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{s.settlementId}</td>
                    <td className="px-3 py-2 text-xs text-gray-500">
                      {s.settlementStart?.slice(0, 10)} — {s.settlementEnd?.slice(0, 10)}
                    </td>
                    <td className="px-3 py-2 text-xs">{s.depositDate?.slice(0, 10)}</td>
                    <td className="px-3 py-2 text-right font-medium">
                      {new Intl.NumberFormat('en-US', { style: 'currency', currency: s.currency || 'USD' }).format(s.totalAmount)}
                    </td>
                    <td className="px-3 py-2">
                      {crossMonth ? (
                        <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-medium text-blue-700">
                          Split
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">No</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {s.previousStatus === 'failed' ? (
                        <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700" title={s.previousError ?? ''}>
                          Failed
                        </span>
                      ) : s.previousStatus === 'voided' ? (
                        <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                          Voided
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">New</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Reconciliation Tab ───────────────────────────────────────────────────────

function ReconciliationTab({ entries }: { entries: ReconciliationEntry[] }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-6">
      <div className="mb-4">
        <h3 className="text-sm font-semibold">Bank Reconciliation</h3>
        <p className="text-xs text-gray-500 mt-0.5">
          Settlement → Journal Entry → Bank Deposit chain. Each settlement's JE total matches the exact bank deposit.
        </p>
      </div>

      {entries.length === 0 ? (
        <p className="text-sm text-gray-500">No settlements found.</p>
      ) : (
        <div className="space-y-3">
          {entries.map(e => {
            const settlementJournals = e.journals.filter(j => j.journalType !== 'cogs');
            const cogsJournal = e.journals.find(j => j.journalType === 'cogs');
            const allSynced = settlementJournals.length > 0 && settlementJournals.every(j => j.status === 'synced');
            const anyVoided = e.journals.some(j => j.voidedAt);

            return (
              <div key={e.settlementId} className={`rounded-lg border p-4 ${
                allSynced && !anyVoided ? 'border-green-200 bg-green-50/30' :
                anyVoided ? 'border-gray-200 bg-gray-50/30' :
                'border-gray-200'
              }`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-xs font-medium">{e.settlementId}</span>
                    <span className="text-xs text-gray-500">
                      {e.settlementStart?.slice(0, 10)} — {e.settlementEnd?.slice(0, 10)}
                    </span>
                  </div>
                  <span className="font-medium text-sm">
                    {new Intl.NumberFormat('en-US', { style: 'currency', currency: e.currency || 'USD' }).format(e.totalAmount)}
                  </span>
                </div>

                {/* Journal entry chain */}
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-gray-400">Deposit {e.depositDate?.slice(0, 10)}</span>
                  <span className="text-gray-300">→</span>

                  {e.journals.length === 0 ? (
                    <span className="text-xs text-gray-400 italic">Not synced</span>
                  ) : (
                    e.journals.map((j, i) => (
                      <span key={i} className="flex items-center gap-1">
                        {i > 0 && <span className="text-gray-300">+</span>}
                        <JournalTypeBadge type={j.journalType} />
                        {j.status === 'synced' && !j.voidedAt ? (
                          <span className="text-xs text-green-700">#{j.qbJournalId}</span>
                        ) : j.voidedAt ? (
                          <span className="text-xs text-gray-400 line-through">#{j.qbJournalId}</span>
                        ) : (
                          <span className="text-xs text-red-600">failed</span>
                        )}
                        {j.journalAmount != null && (
                          <span className="text-[10px] text-gray-400">(${Math.abs(j.journalAmount).toFixed(2)})</span>
                        )}
                      </span>
                    ))
                  )}

                  {allSynced && !anyVoided && (
                    <>
                      <span className="text-gray-300">→</span>
                      <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-medium text-green-700">
                        Bank Match
                      </span>
                    </>
                  )}
                </div>

                {/* COGS note */}
                {cogsJournal && cogsJournal.status === 'synced' && !cogsJournal.voidedAt && (
                  <div className="mt-1 text-[10px] text-gray-500">
                    COGS (FIFO): ${cogsJournal.journalAmount?.toFixed(2)} posted
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Mappings Tab ─────────────────────────────────────────────────────────────

function MappingsTab({
  mappings, qbAccounts, onSave, onDelete, onReset,
}: {
  mappings: FeeMapping[];
  qbAccounts: QBAccount[];
  onSave: (feeType: string, qbId: string, qbName: string) => void;
  onDelete: (id: number) => void;
  onReset: () => void;
}) {
  // Separate system mappings from fee mappings
  const systemMappings = mappings.filter(m => m.amazonFeeType.startsWith('_'));
  const feeMappings = mappings.filter(m => !m.amazonFeeType.startsWith('_'));

  return (
    <div className="space-y-4">
      {/* System Accounts */}
      <div className="rounded-xl border border-gray-200 bg-white p-6">
        <h3 className="text-sm font-semibold mb-3">System Accounts</h3>
        <p className="text-xs text-gray-500 mb-4">Special QB accounts used for settlement clearing, accrual month-split, and COGS posting.</p>
        <div className="space-y-2">
          {systemMappings.map(m => (
            <div key={m.id} className="flex items-center justify-between rounded-lg bg-gray-50 px-4 py-2">
              <div>
                <span className="text-xs font-medium text-gray-700">
                  {m.amazonFeeType === '_CLEARING_' ? 'Amazon Clearing (Bank)' :
                   m.amazonFeeType === '_CARRIED_FORWARD_' ? 'Carried Forward (Accrual)' :
                   m.amazonFeeType === '_COGS_' ? 'Cost of Goods Sold' :
                   m.amazonFeeType === '_INVENTORY_ASSET_' ? 'FBA Inventory Asset' :
                   m.amazonFeeType}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">{m.qbAccountName ?? '—'}</span>
                {qbAccounts.length > 0 && (
                  <select
                    className="rounded border border-gray-200 px-2 py-1 text-xs"
                    value={m.qbAccountId}
                    onChange={(e) => {
                      const acct = qbAccounts.find((a) => a.id === e.target.value);
                      if (acct) onSave(m.amazonFeeType, acct.id, acct.name);
                    }}
                  >
                    <option value={m.qbAccountId}>{m.qbAccountName ?? 'Select...'}</option>
                    {qbAccounts.map((a) => (
                      <option key={a.id} value={a.id}>{a.name} ({a.type})</option>
                    ))}
                  </select>
                )}
              </div>
            </div>
          ))}
          {systemMappings.length === 0 && (
            <p className="text-xs text-gray-400">No system mappings. Click "Reset to Defaults" to create them.</p>
          )}
        </div>
      </div>

      {/* Fee Mappings */}
      <div className="rounded-xl border border-gray-200 bg-white p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold">Amazon Fee → QB Account Mapping</h3>
          <button onClick={onReset} className="text-xs text-brand-600 hover:text-brand-800">
            Reset to Defaults
          </button>
        </div>

        {feeMappings.length === 0 ? (
          <p className="text-sm text-gray-500">No mappings configured. Click "Reset to Defaults" to initialize.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs font-medium uppercase text-gray-500">
                <tr>
                  <th className="px-3 py-2">Amazon Fee Type</th>
                  <th className="px-3 py-2">QB Account</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {feeMappings.map((m) => (
                  <tr key={m.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2 font-mono text-xs">{m.amazonFeeType}</td>
                    <td className="px-3 py-2 text-xs">{m.qbAccountName ?? '—'}</td>
                    <td className="px-3 py-2 text-right">
                      {qbAccounts.length > 0 && (
                        <select
                          className="rounded border border-gray-200 px-1 py-0.5 text-xs"
                          value={m.qbAccountId}
                          onChange={(e) => {
                            const acct = qbAccounts.find((a) => a.id === e.target.value);
                            if (acct) onSave(m.amazonFeeType, acct.id, acct.name);
                          }}
                        >
                          <option value="0">Select...</option>
                          {qbAccounts.map((a) => (
                            <option key={a.id} value={a.id}>{a.name} ({a.type})</option>
                          ))}
                        </select>
                      )}
                      <button onClick={() => onDelete(m.id)} className="ml-2 text-xs text-red-500 hover:text-red-700">
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ── History Tab ──────────────────────────────────────────────────────────────

function HistoryTab({ syncLog, onVoid }: { syncLog: SyncLogEntry[]; onVoid: (id: number) => void }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-6">
      <h3 className="text-sm font-semibold mb-4">Sync History</h3>

      {syncLog.length === 0 ? (
        <p className="text-sm text-gray-500">No sync history yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs font-medium uppercase text-gray-500">
              <tr>
                <th className="px-3 py-2">Settlement ID</th>
                <th className="px-3 py-2">Type</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">QB Journal</th>
                <th className="px-3 py-2">Date</th>
                <th className="px-3 py-2 text-right">Amount</th>
                <th className="px-3 py-2">Synced At</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {syncLog.map((log) => (
                <tr key={log.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2 font-mono text-xs">{log.settlementId}</td>
                  <td className="px-3 py-2">
                    <JournalTypeBadge type={log.journalType} />
                  </td>
                  <td className="px-3 py-2">
                    <StatusBadge status={log.voidedAt ? 'voided' : log.syncStatus} />
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-500">
                    {log.voidedAt ? (
                      <span className="line-through">{log.qbJournalId ?? '—'}</span>
                    ) : (
                      log.qbJournalId ?? '—'
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs">{log.journalDate ?? '—'}</td>
                  <td className="px-3 py-2 text-right text-xs font-medium">
                    {log.journalAmount != null ? `$${Math.abs(log.journalAmount).toFixed(2)}` : '—'}
                  </td>
                  <td className="px-3 py-2 text-xs">{log.syncedAt?.slice(0, 16) ?? '—'}</td>
                  <td className="px-3 py-2 text-right">
                    {log.syncStatus === 'synced' && !log.voidedAt && (
                      <button
                        onClick={() => onVoid(log.id)}
                        className="text-xs text-red-500 hover:text-red-700"
                      >
                        Void
                      </button>
                    )}
                    {log.syncStatus === 'failed' && log.errorMessage && (
                      <span className="text-xs text-red-500" title={log.errorMessage}>Error</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function JournalTypeBadge({ type }: { type: string }) {
  const styles: Record<string, string> = {
    settlement: 'bg-blue-100 text-blue-700',
    settlement_split: 'bg-indigo-100 text-indigo-700',
    cogs: 'bg-purple-100 text-purple-700',
  };
  const labels: Record<string, string> = {
    settlement: 'Settlement',
    settlement_split: 'Month Split',
    cogs: 'COGS',
  };
  return (
    <span className={`ml-1 inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium ${styles[type] ?? 'bg-gray-100 text-gray-600'}`}>
      {labels[type] ?? type}
    </span>
  );
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    synced: 'bg-green-100 text-green-700',
    failed: 'bg-red-100 text-red-700',
    pending: 'bg-yellow-100 text-yellow-700',
    voided: 'bg-gray-100 text-gray-500',
    skipped: 'bg-gray-100 text-gray-600',
  };
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${colors[status] ?? 'bg-gray-100 text-gray-600'}`}>
      {status}
    </span>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-6">
      <div className="h-8 w-48 animate-pulse rounded bg-gray-200" />
      {[1, 2, 3].map((i) => (
        <div key={i} className="h-40 animate-pulse rounded-xl border border-gray-200 bg-gray-100" />
      ))}
    </div>
  );
}
